// Config panel for the "caro_api_request" action block
// Simplified HTTP request with fixed base URL and device auth
import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Paper,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const CaroApiRequestConfig = ({ config, onChange, onSummaryChange }) => {
  const [jsonError, setJsonError] = useState(null);

  const headers = config.headers || [{ key: '', value: '' }];

  const handleChange = (field, value) => {
    const updated = { ...config, [field]: value };
    onChange(updated);
    const method = (updated.method || 'GET').toUpperCase();
    const path = updated.path || '';
    onSummaryChange(`${method} /api/${path}`.substring(0, 40));
  };

  const handleBodyChange = (value) => {
    if (value.trim()) {
      try {
        JSON.parse(value);
        setJsonError(null);
      } catch (e) {
        setJsonError(e.message);
      }
    } else {
      setJsonError(null);
    }
    handleChange('body', value);
  };

  const updateHeaders = (newHeaders) => {
    const updated = { ...config, headers: newHeaders };
    onChange(updated);
  };

  const handleHeaderChange = (index, field, value) => {
    const newHeaders = headers.map((h, i) => i === index ? { ...h, [field]: value } : h);
    updateHeaders(newHeaders);
  };

  const addHeader = () => {
    updateHeaders([...headers, { key: '', value: '' }]);
  };

  const removeHeader = (index) => {
    if (headers.length <= 1) {
      updateHeaders([{ key: '', value: '' }]);
      return;
    }
    updateHeaders(headers.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Sends a request to the CARO API using built-in device authentication.
        Only specify the path after <code>/api/</code>.
      </Typography>

      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>Base URL:</strong> https://api.caro.care/api/<br />
          <strong>Auth:</strong> Basic Auth (deviceUser) — applied automatically
        </Typography>
      </Alert>

      {/* Method + Path */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Method</InputLabel>
          <Select
            value={config.method || 'GET'}
            onChange={(e) => handleChange('method', e.target.value)}
            label="Method"
          >
            <MenuItem value="GET">GET</MenuItem>
            <MenuItem value="POST">POST</MenuItem>
            <MenuItem value="PUT">PUT</MenuItem>
            <MenuItem value="PATCH">PATCH</MenuItem>
            <MenuItem value="DELETE">DELETE</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="API Path"
          value={config.path || ''}
          onChange={(e) => handleChange('path', e.target.value)}
          fullWidth
          required
          placeholder="alarms/active/location/123"
          InputProps={{
            startAdornment: (
              <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5, whiteSpace: 'nowrap' }}>
                /api/
              </Typography>
            ),
          }}
          helperText="Path relative to /api/ — supports {{variables}}"
        />
      </Box>

      {/* Extra Headers */}
      <Accordion variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
            Extra Headers
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {headers.map((header, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Header"
                value={header.key || ''}
                onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                placeholder="X-Custom-Header"
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Value"
                value={header.value || ''}
                onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                placeholder="value"
                sx={{ flex: 1 }}
              />
              <IconButton size="small" onClick={() => removeHeader(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Paper>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addHeader}>
            Add Header
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* JSON Body */}
      <TextField
        size="small"
        label="Request Body (JSON)"
        value={config.body || ''}
        onChange={(e) => handleBodyChange(e.target.value)}
        multiline
        rows={5}
        placeholder={'{\n  "status": "acknowledged",\n  "comment": "Auto-handled"\n}'}
        error={!!jsonError}
        helperText={jsonError || 'JSON payload. Leave empty for GET requests. Supports {{variables}}.'}
        InputProps={{
          sx: { fontFamily: 'monospace', fontSize: '0.8rem' }
        }}
      />

      {/* Response Variable */}
      <TextField
        size="small"
        label="Store Response As Variable"
        value={config.responseVariable || ''}
        onChange={(e) => handleChange('responseVariable', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
        placeholder="e.g. apiResponse"
        helperText={
          config.responseVariable
            ? `Response available as {{${config.responseVariable}.field}}`
            : 'Optional. Variable name to store the parsed JSON response.'
        }
      />

      {/* Timeout */}
      <TextField
        size="small"
        label="Timeout (seconds)"
        type="number"
        value={config.timeout || 10}
        onChange={(e) => handleChange('timeout', parseInt(e.target.value, 10) || 10)}
        inputProps={{ min: 1, max: 60 }}
        helperText="Max wait time (1-60 seconds)"
      />
    </Box>
  );
};

export default CaroApiRequestConfig;
